import { computed, reactive } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch as $httpFetchV1 } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpStore } from '@backmarket/nuxt-module-http/useHttpStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import type { Camelized } from 'humps'
import { defineStore } from 'pinia'

import type { GetUserAuthResponse } from '../api/auth/types'

// this prevents other layers using this composable from having type issues
import type {} from '../api/auth/specs'

import { useAuthStore } from './useAuthStore'

type State = {
  firstName?: string
  lastName?: string
  email?: string
  emailHashed?: string
  isShadowingClient: boolean
  isShadowingMerchant: boolean
  isStaff: boolean
  clientId?: number
  merchantId?: number
  cartItemsCount: number
  lastOrderDate?: Date
  numberOfOrders: number
  phone?: {
    dial: string
    phone: string
  }
  lifetimeRevenue: {
    amount: number
    currency?: string
  }
  detectedCountryCode?: string
  permissions: string[]
}

type PrefetchUserPromise = Promise<Camelized<GetUserAuthResponse>>

const defaultState: State = {
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  emailHashed: undefined,
  isShadowingClient: false,
  isShadowingMerchant: false,
  isStaff: false,
  clientId: undefined,
  merchantId: undefined,
  cartItemsCount: 0,
  lastOrderDate: undefined,
  numberOfOrders: 0,
  phone: undefined,
  lifetimeRevenue: {
    amount: 0,
    currency: undefined,
  },
  detectedCountryCode: undefined,
  permissions: [],
}

/**
 * Pinia store exposing the current authenticated user.
 *
 * For details about the actions, please refer to their own documentation.
 */
export const useUserStore = defineStore('user', () => {
  const authStore = useAuthStore()

  const state = reactive({ ...defaultState })
  const httpStore = useHttpStore()
  const $httpFetch = useHttpFetch()

  /**
   * Prefetch user without filling the related auth store.
   * This is useful to start fetching without interfering with the hydration process.
   */
  async function prefetchUser(): PrefetchUserPromise {
    return camelizeKeys(await $httpFetch('/bm/user/auth'))
  }

  /**
   * Fetch the currently authenticated user information.
   *
   * In order to retrieve all the user information from the Badoom endpoint, a
   * proper `sessionid` cookie must be passed alongside the request. If you are
   * already using the `useLogin` composable to handle the authentication flow,
   * this is transparent for you, and all you have to do is call this action.
   *
   * Note that the `fetchUser` action also takes care of filling the related
   * auth store (retrieved via `useAuthStore`).
   *
   * @example
   * const userStore = useUserStore()
   *
   * userStore.firstName // undefined
   * userStore.lastName // undefined
   *
   * await userStore.fetchUser()
   *
   * userStore.firstName // string
   * userStore.lastName // string
   */
  async function fetchUser(prefetchPromise?: PrefetchUserPromise) {
    const user = prefetchPromise ? await prefetchPromise : await prefetchUser()

    state.firstName = user.firstName
    state.lastName = user.lastName
    state.email = user.username
    state.emailHashed = user.emailHashed
    state.isShadowingClient = user.isStaffShadowingClient
    state.isShadowingMerchant = user.isStaffShadowingMerchant
    state.isStaff = user.isStaff
    state.clientId = user.clientId
    state.merchantId = user.merchantId
    state.cartItemsCount = user.itemsInCartCount || 0
    state.numberOfOrders = user.numberOfOrders || 0
    state.permissions = user.permissions || []

    if (user.countryDialInCode && user.phone) {
      state.phone = {
        dial: user.countryDialInCode,
        phone: user.phone,
      }
    }

    if (user.lifetimeRevenue) {
      state.lifetimeRevenue = user.lifetimeRevenue
    }

    if (user.lastOrderDate) {
      state.lastOrderDate = new Date(user.lastOrderDate)
    }

    authStore.userId = user.id ?? undefined

    if (user.encryptedUserId) {
      authStore.encryptedUserId = user.encryptedUserId
    }
    if (user.bmUserId) {
      authStore.bmUserId = user.bmUserId
    }
    if (user.detectedCountryCode) {
      state.detectedCountryCode = user.detectedCountryCode
    }

    httpStore.csrf = user.csrf
  }

  /**
   * Returns a boolean indicating wether we already tried to retrieve the
   * user information or not.
   *
   * It comes from the authStore that also has the information.
   */
  const { fetched } = authStore

  /**
   * Returns a boolean indicating wether the user is authenticated or not.
   * This is based on the presence of the `email` property.
   */
  const isUserAuthenticated = computed(() => Boolean(state.email))

  /**
   * Returns a boolean indicating wether the user is authenticated as a seller
   * or not. This is based on the presence of the `merchantId` property.
   */
  const isUserAuthenticatedSeller = computed(() => Boolean(state.merchantId))

  function $reset() {
    Object.assign(state, defaultState)
  }

  /**
   * Set email of the user
   */
  function setEmail(email: string) {
    Object.assign(state, { email })
  }

  /**
   * Register a user with the given credentials.
   * @deprecated Use `useLogin` instead. This was added for bo-seller retro-compatibility.
   */
  async function sellerRegister(
    body: merchantsAPI.PostMerchantRegister.Request,
  ) {
    await $httpFetchV1(merchantsAPI.postSellerRegisterExistingUser, { body })
  }

  return {
    user: state,
    // actions
    fetchUser,
    prefetchUser,
    sellerRegister,
    // mutations
    setEmail,
    $reset,
    // getters
    fetched,
    isUserAuthenticated,
    isUserAuthenticatedSeller,
  }
})
